import React, { FC, useCallback, useState } from 'react';
import PropertiesList from 'uikit/propertiesList/PropertiesList';
import styles from './monitoringReviewSection.module.scss';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { ExpandedRowHeader } from '../header/expandedRowHeader.component';
import { AppButton } from 'uikit/buttons';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { IPatientListDto, MonitoringReviewStatusEnum, StudyRoleClaimValue } from 'src/services/api/api-client';
import { Chips } from 'uikit/chips/chips.component';
import { reviewPatient, revokePatientReview } from 'src/services/api/api-client/PatientClient';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { useQueryClient } from '@tanstack/react-query';
import { getPatientQueryKey, getPatientsQueryKey } from 'src/services/api/api-client/PatientQuery';
import { ChipsProps } from 'uikit/chips/chips';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { Trans } from 'react-i18next';

type MonitoringReviewSectionProps = {
  patient: IPatientListDto;
};

export const MonitoringReviewSection = React.memo(({ patient }: MonitoringReviewSectionProps) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.MonitoringReview');
  const { hasStudyClaim } = useHasStudyClaims();
  const { markAsReviewed, revokeReview, processing } = useMonitoringReviewActions({ patientId: patient.id });

  return (
    <div>
      <ExpandedRowHeader text={t('Header')} />
      <PropertiesList>
        <PropertiesList.Property
          name={t('StatusRow')}
          nameClassName={styles.statusName}
          value={<PatientMonitoringReviewStates patient={patient} />}
        />
        {hasStudyClaim(StudyRoleClaimValue.PatientReviewApproval) && (
          <div className={styles.actionButtonContainer}>
            {patient.monitoringReviewStatus === MonitoringReviewStatusEnum.ReadyForReview && (
              <AppButton
                colorSchema={'primary'}
                variant={'icon-link'}
                text={t('Actions.MarkAsReviewed')}
                onClick={markAsReviewed}
                isLoading={processing}
                loadingText={t('Actions.ProcessingText')}
              />
            )}
            {patient.monitoringReviewStatus === MonitoringReviewStatusEnum.ReadyForRelease && (
              <AppButton
                colorSchema={'primary'}
                variant={'icon-link'}
                text={t('Actions.RevokeReview')}
                onClick={revokeReview}
                isLoading={processing}
                loadingText={t('Actions.ProcessingText')}
              />
            )}
          </div>
        )}
      </PropertiesList>
    </div>
  );
});

const PatientMonitoringReviewStates: FC<{ patient: IPatientListDto }> = ({ patient }) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.MonitoringReview');

  const obj: Record<
    MonitoringReviewStatusEnum,
    { text: string; colorSchema: ChipsProps['colorSchema']; tooltip?: React.ReactNode }
  > = {
    [MonitoringReviewStatusEnum.Released]: {
      text: t('States.Released'),
      colorSchema: 'green',
    },
    [MonitoringReviewStatusEnum.ReadyForRelease]: {
      text: t('States.ReadyForRelease'),
      colorSchema: 'green',
    },
    [MonitoringReviewStatusEnum.ReadyForReview]: {
      text: t('States.ReadyForReview'),
      colorSchema: 'blue',
    },
    [MonitoringReviewStatusEnum.NotReady]: {
      text: t('States.NotReady'),
      colorSchema: 'default',
      tooltip: (
        <Trans
          t={t}
          i18nKey={'Tooltips.NotReadyTooltip'}
          components={{
            bold: <span className={styles.boldText} />,
          }}
        />
      ),
    },
  };

  const stateData = obj[patient.monitoringReviewStatus];

  return (
    <Tooltip tooltipContent={stateData.tooltip} disabled={!stateData.tooltip}>
      <Chips className={styles.chip} colorSchema={stateData.colorSchema} text={stateData.text} />
    </Tooltip>
  );
};

const useMonitoringReviewActions = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  const [processing, setProcessing] = useState(false);

  const markAsReviewedHandler = useCallback(async () => {
    try {
      setProcessing(true);
      await reviewPatient(patientId);
      await Promise.all([
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getPatientQueryKey(patientId)),
      ]);
    } catch (error) {
      showServerErrorToast(error);
    } finally {
      setProcessing(false);
    }
  }, [patientId, queryClient]);

  const revokeReviewHandler = useCallback(async () => {
    try {
      setProcessing(true);
      await revokePatientReview(patientId);
      await Promise.all([
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getPatientQueryKey(patientId)),
      ]);
    } catch (error) {
      showServerErrorToast(error);
    } finally {
      setProcessing(false);
    }
  }, [patientId, queryClient]);

  return {
    processing: processing,
    markAsReviewed: markAsReviewedHandler,
    revokeReview: revokeReviewHandler,
  };
};
